import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { ActionAreaBtn } from './ActionAreaFab';
import { AiConversationWithMessages, useAiConversationDeleteMutation, useAiConversationsListQuery, useLazyAiConversationGetQuery } from '../services/ai';

const formatConversationDate = (iso: string) => {
	const date = dayjs(iso);
	return date.isSame(dayjs(), 'day') ? date.format('HH:mm') : date.format('D MMM YYYY');
};

export const AskAIConversationsButton = ({ isAuthenticated, onOpenConversation }: { isAuthenticated: boolean, onOpenConversation: (conversation: AiConversationWithMessages) => void }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const { data: conversations = [], isLoading } = useAiConversationsListQuery(undefined, { skip: !isAuthenticated || !anchorEl });
	const [loadConversation, { isFetching: isOpeningConversation }] = useLazyAiConversationGetQuery();
	const [deleteConversation] = useAiConversationDeleteMutation();

	const openConversation = async (id: string) => {
		const conversation = await loadConversation(id).unwrap();
		setAnchorEl(null);
		onOpenConversation(conversation);
	};

	return (
		<>
			<Tooltip title='Past conversations' disableInteractive>
				<span>
					<ActionAreaBtn onClick={(event) => setAnchorEl(event.currentTarget)} disabled={!isAuthenticated} aria-label='Past conversations'>
						<HistoryIcon />
					</ActionAreaBtn>
				</span>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 420 } } }}
			>
				<Typography variant='overline' color='text.secondary' sx={{ px: 2 }}>
					Past conversations
				</Typography>
				{isLoading && (
					<Box display='flex' justifyContent='center' py={2}>
						<CircularProgress size={24} />
					</Box>
				)}
				{!isLoading && conversations.length === 0 && (
					<Typography variant='body2' color='text.secondary' sx={{ px: 2, py: 1 }}>
						No conversations yet. Questions you ask will appear here.
					</Typography>
				)}
				{conversations.map((conversation) => (
					<MenuItem
						key={conversation.id}
						onClick={() => openConversation(conversation.id)}
						disabled={isOpeningConversation}
						sx={{ gap: 1 }}
					>
						<ListItemText
							primary={conversation.title}
							secondary={formatConversationDate(conversation.updatedAt)}
							slotProps={{ primary: { noWrap: true } }}
						/>
						<Tooltip title='Delete conversation' disableInteractive>
							<IconButton
								size='small'
								edge='end'
								aria-label='Delete conversation'
								onClick={(event) => {
									event.stopPropagation();
									deleteConversation(conversation.id);
								}}
							>
								<DeleteOutlineIcon fontSize='small' />
							</IconButton>
						</Tooltip>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};
